// Countries the SDR BC deck can actually render.
//
// ⚠️ MUST stay in sync with `COUNTRY_REGION` in
// `supabase/functions/_shared/regions.ts`. The research pipeline
// (`sdr-bc-research/index.ts`) drops any country whose ISO is not in that map
// (`regionOf(iso) → null → skip`), so offering an unsupported country in the
// emergency-mode picker would silently vanish from the deck and break the
// share math. We therefore expose ONLY the supported markets.
//
// Region buckets mirror `REGION_LABEL` in regions.ts:
//   us → North America · lat → LATAM · ema → EMEA · apa → APAC

export type SdrBcRegion = 'North America' | 'LATAM' | 'EMEA' | 'APAC'

export interface SdrBcCountry {
  iso: string // ISO 3166-1 alpha-2 (matches a COUNTRY_REGION key)
  name: string // English display name
  region: SdrBcRegion
}

// Order within a region is roughly by market relevance for outreach, matching
// the grouping comments in regions.ts. Display order in the picker groups by
// region (North America → LATAM → EMEA → APAC).
export const SDR_BC_COUNTRIES: SdrBcCountry[] = [
  // ── North America (us) — US + Canada only ──
  { iso: 'US', name: 'United States', region: 'North America' },
  { iso: 'CA', name: 'Canada', region: 'North America' },

  // ── LATAM (lat) — Mexico + Central America + Caribbean ──
  { iso: 'MX', name: 'Mexico', region: 'LATAM' },
  { iso: 'BR', name: 'Brazil', region: 'LATAM' },
  { iso: 'AR', name: 'Argentina', region: 'LATAM' },
  { iso: 'CO', name: 'Colombia', region: 'LATAM' },
  { iso: 'CL', name: 'Chile', region: 'LATAM' },
  { iso: 'PE', name: 'Peru', region: 'LATAM' },
  { iso: 'EC', name: 'Ecuador', region: 'LATAM' },
  { iso: 'UY', name: 'Uruguay', region: 'LATAM' },
  { iso: 'PY', name: 'Paraguay', region: 'LATAM' },
  { iso: 'BO', name: 'Bolivia', region: 'LATAM' },
  { iso: 'VE', name: 'Venezuela', region: 'LATAM' },
  { iso: 'DO', name: 'Dominican Republic', region: 'LATAM' },
  { iso: 'PR', name: 'Puerto Rico', region: 'LATAM' },
  { iso: 'CR', name: 'Costa Rica', region: 'LATAM' },
  { iso: 'PA', name: 'Panama', region: 'LATAM' },
  { iso: 'GT', name: 'Guatemala', region: 'LATAM' },
  { iso: 'HN', name: 'Honduras', region: 'LATAM' },
  { iso: 'SV', name: 'El Salvador', region: 'LATAM' },
  { iso: 'NI', name: 'Nicaragua', region: 'LATAM' },
  { iso: 'JM', name: 'Jamaica', region: 'LATAM' },
  { iso: 'BS', name: 'Bahamas', region: 'LATAM' },
  { iso: 'TT', name: 'Trinidad & Tobago', region: 'LATAM' },

  // ── EMEA (ema) — Europe ──
  { iso: 'GB', name: 'United Kingdom', region: 'EMEA' },
  { iso: 'DE', name: 'Germany', region: 'EMEA' },
  { iso: 'FR', name: 'France', region: 'EMEA' },
  { iso: 'ES', name: 'Spain', region: 'EMEA' },
  { iso: 'IT', name: 'Italy', region: 'EMEA' },
  { iso: 'NL', name: 'Netherlands', region: 'EMEA' },
  { iso: 'BE', name: 'Belgium', region: 'EMEA' },
  { iso: 'CH', name: 'Switzerland', region: 'EMEA' },
  { iso: 'AT', name: 'Austria', region: 'EMEA' },
  { iso: 'PT', name: 'Portugal', region: 'EMEA' },
  { iso: 'IE', name: 'Ireland', region: 'EMEA' },
  { iso: 'SE', name: 'Sweden', region: 'EMEA' },
  { iso: 'NO', name: 'Norway', region: 'EMEA' },
  { iso: 'DK', name: 'Denmark', region: 'EMEA' },
  { iso: 'FI', name: 'Finland', region: 'EMEA' },
  { iso: 'PL', name: 'Poland', region: 'EMEA' },
  { iso: 'CZ', name: 'Czech Republic', region: 'EMEA' },
  { iso: 'SK', name: 'Slovakia', region: 'EMEA' },
  { iso: 'HU', name: 'Hungary', region: 'EMEA' },
  { iso: 'RO', name: 'Romania', region: 'EMEA' },
  { iso: 'GR', name: 'Greece', region: 'EMEA' },
  { iso: 'TR', name: 'Türkiye', region: 'EMEA' },
  // EMEA — Middle East
  { iso: 'AE', name: 'United Arab Emirates', region: 'EMEA' },
  { iso: 'SA', name: 'Saudi Arabia', region: 'EMEA' },
  { iso: 'QA', name: 'Qatar', region: 'EMEA' },
  { iso: 'KW', name: 'Kuwait', region: 'EMEA' },
  { iso: 'BH', name: 'Bahrain', region: 'EMEA' },
  { iso: 'OM', name: 'Oman', region: 'EMEA' },
  { iso: 'JO', name: 'Jordan', region: 'EMEA' },
  { iso: 'IL', name: 'Israel', region: 'EMEA' },
  { iso: 'LB', name: 'Lebanon', region: 'EMEA' },
  { iso: 'EG', name: 'Egypt', region: 'EMEA' },
  // EMEA — Africa
  { iso: 'ZA', name: 'South Africa', region: 'EMEA' },
  { iso: 'NG', name: 'Nigeria', region: 'EMEA' },
  { iso: 'KE', name: 'Kenya', region: 'EMEA' },
  { iso: 'MA', name: 'Morocco', region: 'EMEA' },
  { iso: 'GH', name: 'Ghana', region: 'EMEA' },
  { iso: 'CI', name: "Côte d'Ivoire", region: 'EMEA' },
  { iso: 'TZ', name: 'Tanzania', region: 'EMEA' },
  { iso: 'TN', name: 'Tunisia', region: 'EMEA' },
  { iso: 'ET', name: 'Ethiopia', region: 'EMEA' },

  // ── APAC (apa) ──
  { iso: 'JP', name: 'Japan', region: 'APAC' },
  { iso: 'IN', name: 'India', region: 'APAC' },
  { iso: 'CN', name: 'China', region: 'APAC' },
  { iso: 'AU', name: 'Australia', region: 'APAC' },
  { iso: 'NZ', name: 'New Zealand', region: 'APAC' },
  { iso: 'SG', name: 'Singapore', region: 'APAC' },
  { iso: 'HK', name: 'Hong Kong', region: 'APAC' },
  { iso: 'KR', name: 'South Korea', region: 'APAC' },
  { iso: 'TW', name: 'Taiwan', region: 'APAC' },
  { iso: 'ID', name: 'Indonesia', region: 'APAC' },
  { iso: 'TH', name: 'Thailand', region: 'APAC' },
  { iso: 'VN', name: 'Vietnam', region: 'APAC' },
  { iso: 'PH', name: 'Philippines', region: 'APAC' },
  { iso: 'MY', name: 'Malaysia', region: 'APAC' },
  { iso: 'PK', name: 'Pakistan', region: 'APAC' },
  { iso: 'BD', name: 'Bangladesh', region: 'APAC' },
  { iso: 'LK', name: 'Sri Lanka', region: 'APAC' },
]

// Region display order for the grouped picker.
export const SDR_BC_REGION_ORDER: SdrBcRegion[] = ['North America', 'LATAM', 'EMEA', 'APAC']

export const SDR_BC_COUNTRY_BY_ISO: Record<string, SdrBcCountry> = Object.fromEntries(
  SDR_BC_COUNTRIES.map(c => [c.iso, c]),
)

// Regional-indicator flag emoji from an ISO-2 code (US → 🇺🇸). Returns '' for
// anything that isn't two A–Z letters, so unknown/legacy codes render cleanly.
export function countryFlagEmoji(iso: string | null | undefined): string {
  const code = (iso || '').trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(code)) return ''
  const BASE = 0x1f1e6 // regional indicator 'A'
  return String.fromCodePoint(
    BASE + code.charCodeAt(0) - 65,
    BASE + code.charCodeAt(1) - 65,
  )
}

// Fold diacritics + lowercase for accent-insensitive search
// ("turkiye" → matches "Türkiye", "cote" → "Côte d'Ivoire").
export function normalizeForSearch(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining diacritical marks
    .trim()
}
